import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Col, Row} from "reactstrap";
import {makeStyles} from "@mui/styles";
import {ProductPropertyGroupRecordLeafTypes, ProductTechnicalFileTypes,} from "../../../../../../../core/constants/enums";
import {Tab, Tabs} from "../../../../../../components/base/Tabs";
import {CloudDownload, UnfoldLessRounded, UnfoldMoreRounded} from "@mui/icons-material";
import {ReactComponent as Html} from "../../../../../../../assets/images/file-types/html.svg";
import {ReactComponent as Pdf} from "../../../../../../../assets/images/file-types/pdf.svg";
import {ReactComponent as Image} from "../../../../../../../assets/images/file-types/image.svg";
import {ReactComponent as Unknown} from "../../../../../../../assets/images/file-types/unknown.svg";
import FilePreviewModal, {FilePreviewTypes} from "../../../../../../components/dialogs/file-preview";
import IconButton from "../../../../../../components/base/button/icon_button";
import Utils, {createUUId, downloadFile, numComparator} from "../../../../../../../core/services/utils/utils";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import axios from "axios";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes} from "../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import Api from "../../../../../../../core/services/api/api";
import {Box, Card, Typography} from "@mui/material";
import Chips, {ChipsContentType, ChipsIconPosition, ChipsTypes} from "../../../../../../components/base/chips/chips";
import ProductChipsCodeContent from "../../../../../../components/app-specific/chips-contents/product-code-content";
import Button from "../../../../../../components/base/button/button";
import {useCachedPropertyGroupsController} from "../../../../../../hooks/use-industry";
import classnames from "classnames";
import LoadingLottieAnimation from "../../../../../../components/app-specific/lottie-animations/loading";
import Grid2 from "@mui/material/Unstable_Grid2";
import TryAgain from "../../../../../../components/app-specific/try-again";

const useStyles = makeStyles(() => ({
    section: {
        padding: 10,
        borderRadius: 10,
    },
}))

const tabs = {
    technicalInfo: 'technicalInfo',
    description: 'description',
    notes: 'notes',
    technicalFiles: 'technicalFiles',
    codes: 'codes',
}


/**
 * Groups raw part code values by category for display.
 * @param {Array<{partCodeCategoryId: number, partCodeCategoryTitle: string, id: number, code: string}>} codes
 * @return {{id: number, title: string, codes: {id: number, code: string}[]}[]}
 */
const groupPartCodeValues = (codes) => {
    const groups = {};
    for (const code of codes ?? []) {
        groups[code.partCodeCategoryId] = {
            id: code.partCodeCategoryId,
            title: code.partCodeCategoryTitle,
            codes: [...(groups[code.partCodeCategoryId]?.codes ?? []), {id: code.id, code: code.code}],
        };
    }
    return Object.values(groups);
}

const LookupProductInformationViewInfoSection = ({part, className, id, loading, industryId, addSection, removeSection, shown}) => {
    const classes = useStyles();
    const [tab, setTab] = useState(tabs.technicalInfo);
    const [loadingProperties, setLoadingProperties] = useState(true);
    const [propertyGroups, setPropertyGroups] = useState([]);
    const [propertyOrders, setPropertyOrders] = useState([])
    const [codeGroups, setCodeGroups] = useState([]);
    const isMounted = useIsMounted();
    const {getPropertyGroupsForIndustry} = useCachedPropertyGroupsController();

    const partId = useMemo(() => part?.id, [part?.id]);
    const hasCodes = useMemo(() => codeGroups.length > 0, [codeGroups]);

    /**
     * Fetches the property orders of the industry.
     * @type {(function(*): Promise<void>)}
     */
    const fetchPropertyOrders = useCallback(async (industryId, cancelToken) => {
        // TODO: product ordering API.
        const response = {
            resultFlag: true,
            data: {
                propertiesOrders: [114, 30, 11, 22]
            }
        }
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setPropertyOrders(response?.data?.propertiesOrders ?? []);
        }
    }, [isMounted])

    /**
     * When the industry changes, fetch the property orders.
     */
    useEffect(() => {
        if (!industryId)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void fetchPropertyOrders(industryId, cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [industryId, fetchPropertyOrders])


    /**
     * Fetches the property groups of the products from the server.
     * @type {(function(*, *): Promise<void>)}
     */
    const getPropertyGroups = useCallback(async (industryId, cancelTokenSource) => {
        setLoadingProperties(true);
        const propertyGroups = await getPropertyGroupsForIndustry(industryId, cancelTokenSource.token);
        if (!isMounted())
            return;
        if (!cancelTokenSource.token?.reason) {
            setLoadingProperties(false);
        }
        setPropertyGroups(propertyGroups ?? []);
    }, [getPropertyGroupsForIndustry, isMounted])

    /**
     * As soon as the component is mounted:
     * - fetches the technical properties of the part.
     */
    useEffect(() => {
        if (!industryId)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void getPropertyGroups(industryId, cancelTokenSource);
        return () => cancelTokenSource.cancel();
    }, [getPropertyGroups, industryId])

    /**
     * Prefetches part code values when the part id is available so the Codes tab can be hidden when empty.
     */
    const getCodeGroups = useCallback(async (partId) => {
        setCodeGroups([]);
        const response = await Api.listPartCodeValuesForView(partId);
        if (!isMounted())
            return;
        if (response?.resultFlag && (response?.data?.length ?? 0) > 0) {
            setCodeGroups(groupPartCodeValues(response.data));
        } else {
            setCodeGroups([]);
        }
    }, [isMounted])

    /**
     * With each change in partId:
     * - prefetches product codes for the Codes tab visibility.
     */
    useEffect(() => {
        if (!partId)
            return;
        void getCodeGroups(partId);
    }, [getCodeGroups, partId])

    /**
     * When codes become unavailable, leave the Codes tab if it was selected.
     */
    useEffect(() => {
        if (tab === tabs.codes && !hasCodes) {
            setTab(tabs.technicalInfo);
        }
    }, [tab, hasCodes])

    if (!shown) {
        return null;
    }

    return (
        <Row id={id} className={`${className} no-gutters w-100`}>
            <Col xs={12}>
                <Tabs value={tab ?? false} onChange={(e, tab) => setTab(tab)}>
                    <Tab value={tabs.technicalInfo} label="Technical Information"/>
                    {
                        part?.description &&
                        <Tab value={tabs.description} label="Description"/>
                    }
                    {
                        !loadingProperties &&
                        propertyGroups.map((propertyGroup) => (
                            <Tab key={propertyGroup.id} value={`property-group-${propertyGroup.id}`}
                                 label={propertyGroup.title}/>
                        ))
                    }
                    {
                        part?.notes?.length > 0 &&
                        <Tab value={tabs.notes} label="Notes"/>
                    }
                    {
                        part?.technicalFiles?.length > 0 &&
                        <Tab value={tabs.technicalFiles} label="Technical Files"/>
                    }
                    {
                        hasCodes &&
                        <Tab value={tabs.codes} label="Codes"/>
                    }
                </Tabs>
                <div className={'my-3 w-100'}/>
                <div className={classes.section} style={{maxWidth: '100%', overflowX: 'auto'}}>
                    {
                        tab === tabs.description && part?.description &&
                        <Description loading={loading} description={part?.description}/>
                    }
                    {
                        tab === tabs.technicalInfo &&
                        <TechnicalInfo partId={partId} loading={loading} propertyOrders={propertyOrders}/>
                    }
                    {
                        tab.startsWith('property-group-') &&
                        <PropertyGroupInfo partId={partId} groupId={tab.replace('property-group-', '')}/>
                    }
                    {
                        tab === tabs.notes && part?.notes?.length > 0 &&
                        <Note loading={loading} notes={part?.notes}/>
                    }
                    {
                        tab === tabs.technicalFiles && part?.technicalFiles?.length > 0 &&
                        <TechnicalFiles loading={loading} files={part?.technicalFiles}/>
                    }
                    {
                        tab === tabs.codes && hasCodes &&
                        <Codes loading={loading} codeGroups={codeGroups}/>
                    }
                </div>
            </Col>
        </Row>
    );

}


const Codes = ({loading, codeGroups}) => {
    return (
        <>
            <Row className='no-gutters justify-content-start align-items-center py-3'>
                {
                    loading &&
                    Array.from({length: 2}).map((_, e) => (
                        <Col key={`loading-${e}`} xl={4} lg={6} xs={12} className="p-2">
                            <div className={'loading extraLarge'}>
                                <div/>
                            </div>
                        </Col>
                    ))
                }
                {
                    !loading &&
                    codeGroups?.map((e) => (
                        <Col xl={4} lg={6} xs={12} key={e.id}>
                            <Card variant={'outlined'}
                                  sx={{
                                      margin: 2,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      padding: 2,
                                      // backgroundColor: 'background.tertiary'
                                  }}>
                                <Typography variant={'subtitle1'} fontWeight={600} className={'text-color-1'}>
                                    {e.title}
                                </Typography>
                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, paddingBlock: 2}}>
                                    {
                                        e.codes.map((code) => (
                                            <Chips
                                                key={code.id}
                                                contentType={ChipsContentType.element}
                                                type={ChipsTypes.none}
                                                iconPosition={ChipsIconPosition.start}
                                                onIconClick={(e) => e.stopPropagation() || e.preventDefault()}
                                                buttonProps={{size: 'small',}}
                                                content={(hovering) => <ProductChipsCodeContent code={code} canCopy hovering={hovering}/>}
                                            />
                                        ))
                                    }
                                </Box>
                            </Card>
                        </Col>
                    ))
                }
            </Row>
        </>
    );
}


const TechnicalInfo = ({loading: loadingProduct, partId, propertyOrders}) => {
    const [propertyValues, setPropertyValues] = useState([]);
    const [loadingProperties, setLoadingProperties] = useState(true);
    const isMounted = useIsMounted();

    const loading = useMemo(() => loadingProduct || loadingProperties, [loadingProduct, loadingProperties]);

    /**
     * Fetches the technical properties of the part.
     */
    const getTechnicalProperties = useCallback(async (cancelToken) => {
        setLoadingProperties(true);
        const response = await Api.listPartProperties(partId, cancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoadingProperties(false);
        }
        if (response?.resultFlag) {
            setPropertyValues(response?.data ?? []);
        }
    }, [isMounted, partId])

    /**
     * Fetches the technical properties of the part.
     */
    useEffect(() => {
        if (!partId)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void getTechnicalProperties(cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [getTechnicalProperties, partId])

    const properties = useMemo(() => {
        const props = {};
        for (const propertyValue of propertyValues) {
            props[propertyValue.property.id] = {
                ...props[propertyValue.property.id],
                ...propertyValue.property,
                values: [...(props[propertyValue.property.id]?.values ?? []), Utils.formatTechnicalPropertyValueDisplay(propertyValue.property.industryPropertyTypeId, propertyValue.value)].filter(Boolean),
            };
        }
        const res = Object.values(props).filter(e => e.values?.length > 0);
        const ordered = [];
        for (const id of propertyOrders) {
            const found = res.find((item) => item.id === id);
            if (found)
                ordered.push(found);
        }
        const rest = res.filter((item) => !ordered.includes(item));
        rest.sort((a, b) => a.title.localeCompare(b.title));
        ordered.push(...rest);
        return ordered;
    }, [propertyValues, propertyOrders])

    if (loading) {
        return (
            <LoadingLottieAnimation width={'50%'}/>
        );
    }

    return (
        <Grid2 container>
            {
                properties.map((property, index, array) => (
                    <Grid2 key={property.id} xs={12} md={6} className={'px-3'}>
                        <Box className='d-flex flex-wrap align-items-stretch justify-content-start h-100'>
                            <Grid2
                                xs={6} className={classnames('px-3 py-3 background-tertiary')}
                                sx={{
                                    borderTopLeftRadius: {
                                        xs: index === 0 ? 10 : 0,
                                        md: (index === 0 || index === 1) ? 10 : 0,
                                    },
                                    borderBottomLeftRadius: {
                                        xs: index === array.length - 1 ? 10 : 0,
                                        md: (
                                            (index + 1) % 2 === 0 && (index === array.length - 2 || index === array.length - 1) ||
                                            (index) % 2 === 0 && (index === array.length - 1 || index === array.length - 2)
                                        ) ? 10 : 0,
                                    },
                                }}
                            >
                                <p className='text-left font-weight-semi-bold text-sm text-color-1 mr-4'>
                                    {property.title}:
                                </p>
                            </Grid2>
                            <Grid2
                                xs={6} className={classnames('px-3 py-3 background-default')}
                                sx={{
                                    borderTopRightRadius: {
                                        xs: index === 0 ? 10 : 0,
                                        md: (index === 0 || index === 1) ? 10 : 0,
                                    },
                                    borderBottomRightRadius: {
                                        xs: index === array.length - 1 ? 10 : 0,
                                        md: (
                                            (index + 1) % 2 === 0 && (index === array.length - 2 || index === array.length - 1) ||
                                            (index) % 2 === 0 && (index === array.length - 1 || index === array.length - 2)
                                        ) ? 10 : 0,
                                    },
                                }}>
                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center'}}>
                                    {property.values.map((v, i) => (
                                        <p key={i} className='text-left text-sm text-color-1 m-0'>
                                            {v}
                                            {i < property.values.length - 1 && (typeof v === 'string' || typeof v === 'number') && ","}
                                        </p>
                                    ))}
                                </Box>
                            </Grid2>
                        </Box>
                    </Grid2>
                ))
            }
            {
                properties.length === 0 &&
                <TryAgain
                    showButton={false}
                    text={"This product has no technical properties at this moment."}
                />
            }
        </Grid2>
    );
}

const PropertyGroupInfo = ({partId, groupId}) => {
    const [expandingAll, setExpandingAll] = useState(false);
    const [collapsingAll, setCollapsingAll] = useState(false);
    const [structureSchema, setStructureSchema] = useState([]);
    const [records, setRecords] = useState([]);
    const [loadingRecords, setLoadingRecords] = useState(true);
    const [loadingSchema, setLoadingSchema] = useState(true);
    const isMounted = useIsMounted();
    const {getPropertyGroupAttributes} = useCachedPropertyGroupsController();


    /**
     * Fetches the structure of the property group.
     * @type {(function(*): Promise<void>)}
     */
    const getPropertyGroupStructure = useCallback(async (cancelTokenSource) => {
        setLoadingSchema(true);
        const attributes = await getPropertyGroupAttributes(groupId, cancelTokenSource.token)
        if (!isMounted())
            return;
        if (!cancelTokenSource?.token?.reason) {
            setLoadingSchema(false);
        }
        if (!!attributes) {
            const parsedStructureSchema = [...(attributes ?? [])];
            parsedStructureSchema.sort((a, b) => numComparator(a.orderIndex, b.orderIndex));
            setStructureSchema(parsedStructureSchema.map((e, i, arr) => ({
                groupPropertyId: e.id,
                title: e.title,
                propertyName: e.name,
                nextPropertyName: arr[i + 1]?.name ?? null,
                type: e.industryGroupPropertyAttrTypeId,
                isRequired: e.isRequired,
                nullReplacement: e.nullReplacement,
            })));
        }
    }, [isMounted, groupId, getPropertyGroupAttributes])

    /**
     * As soon as the component mounts:
     * - fetches the structure of the property group.
     */
    useEffect(() => {
        const axiosCancelTokenSource = axios.CancelToken.source();
        void getPropertyGroupStructure(axiosCancelTokenSource);
        return () => axiosCancelTokenSource.cancel();
    }, [getPropertyGroupStructure]);


    /**
     * Fetches the records of the property group.
     * @type {(function(*): Promise<void>)}
     */
    const getPropertyGroupRecords = useCallback(async (cancelToken) => {
        setLoadingRecords(true);
        const response = await Api.listPartIndustryGroupProperties(groupId, partId, cancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoadingRecords(false);
        }
        if (response?.resultFlag) {
            setRecords(response?.data?.map(e => ({
                ...e,
                data: e.content && e.content?.trim()?.length ? JSON.parse(e.content) : {},
                _newRecordPropertyNames: null, // an array of names that would suggest which properties to make editable
                _newRecordValues: [],// keys of properties to be edited, and their new values
            })) ?? []);
        }
    }, [isMounted, groupId, partId])

    /**
     * Fetches the records of the property group.
     */
    useEffect(() => {
        const axiosCancelTokenSource = axios.CancelToken.source();
        void getPropertyGroupRecords(axiosCancelTokenSource.token);
        return () => axiosCancelTokenSource.cancel();
    }, [getPropertyGroupRecords])

    /**
     * Groups the records based on the schema.
     */
    const groupRecords = useCallback((records, schema, currentLevel = 0, parentCommonId = '') => {
        if (currentLevel >= schema.length)
            return records; // Base case: return records if schema is exhausted

        const {propertyName, nextPropertyName} = schema[currentLevel];

        let newRecords = [];
        // Create a map to hold the grouped records
        const groupsMap = records.reduce((acc, record) => {
            const groupValue = record.data[propertyName]; // Group by the title of the current property
            if (!groupValue && record._newRecordPropertyNames) {
                newRecords.push(record);
                return acc;
            }

            if (!acc[groupValue]) {
                const commonId = parentCommonId ? `${parentCommonId}-${groupValue}` : ((groupValue?.toString()) ?? schema[currentLevel]?.nullReplacement ?? createUUId());
                acc[groupValue] = {
                    commonId: commonId,
                    schema: schema[currentLevel],
                    value: groupValue,
                    approved: record.approved,
                    children: [],
                    counts: {}, // Dynamic counts object to hold counts for inner properties only
                    titles: {},
                };
            }

            acc[groupValue].children.push(record);

            return acc;
        }, {});

        // Now, for each group, recursively group by the next property
        const groups = Object.values(groupsMap).map(group => {

            if (!schema[currentLevel + 1]?.nextPropertyName) {
                // parent of leaves
                return {
                    commonId: group.commonId, // Pass the commonId along
                    schema: group.schema,
                    value: group.value,
                    approved: group.approved,
                    records: group.children, // list of records
                    counts: {
                        [`${nextPropertyName}Count`]: group.children.length,
                    }, // Store the count for the leaf level
                    titles: {
                        [`${nextPropertyName}Title`]: [...new Set(group.children.map(e => e.data[nextPropertyName]))],
                    },
                };
            }

            const groupedChildren = groupRecords(group.children, schema, currentLevel + 1, group.commonId) // Recursive call for the next level

            // Collect counts dynamically based on schema depth (excluding the current level)
            const counts = groupedChildren.reduce((totalCounts, child) => {
                // Sum counts from child groups if available
                if (child.counts) {
                    for (const countKey in child.counts) {
                        totalCounts[countKey] = (totalCounts[countKey] || 0) + child.counts[countKey];
                    }
                }
                if (child.records) {
                    const lastPropertyName = schema[schema.length - 1]?.propertyName ?? "";
                    totalCounts[`${lastPropertyName}Count`] = (totalCounts[`${lastPropertyName}Count`] || 0) + child.records.length;
                }
                return totalCounts;
            }, {});

            counts[`${nextPropertyName}Count`] = groupedChildren.length;

            // Collect counts dynamically based on schema depth (excluding the current level)
            const titles = groupedChildren.reduce((totalTitles, child) => {
                // Sum counts from child groups if available
                if (child.titles) {
                    for (const countKey in child.titles) {
                        totalTitles[countKey] = [...new Set([...(totalTitles[countKey] ?? []), ...child.titles[countKey]])];
                    }
                }
                return totalTitles;
            }, {});

            titles[`${nextPropertyName}Title`] = [...new Set(group.children.map(e => e.data[nextPropertyName]))];

            return {
                commonId: group.commonId,
                schema: group.schema,
                value: group.value,
                approved: group.approved,
                counts: counts, // Store the calculated counts for inner levels
                titles: titles,
                children: groupedChildren, // Grouped children or records
            };
        });

        if (newRecords) {
            return [...groups, ...newRecords];
        }

        return groups;
    }, []);

    const groupedRecords = useMemo(() => groupRecords(records, structureSchema), [groupRecords, records, structureSchema]);

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            ...structureSchema.map((e) => ({
                name: e.propertyName,
                title: e.title,
                type: DataGridColumnTypes.element,
                alignment: DataGridColumnAlignments.left,
                pinned: false,
                sortable: false,
                resizable: false,
                pinnedToggleable: false,
                reorderable: false,
                visibilityToggleable: false,
                savable: false,
                width: 180,
            })),
            ...structureSchema.map((e) => ({
                name: `__${e.propertyName}`,
                visible: false,
                pinned: false,
                sortable: false,
                resizable: false,
                pinnedToggleable: false,
                reorderable: false,
                visibilityToggleable: false,
                savable: false,
                width: 0,
            })),
        ], [structureSchema])

    /**
     * Groups the range records.
     * @type {(function(*, *): ([]|*[]))}
     */
    const groupRangeRecords = useCallback((_records, schema) => {
        if (!_records.length)
            return [];

        const records = _records.map(e => ({
            ...e,
            data: {
                ...e.data,
                [schema.propertyName]: (e.data[schema.propertyName] === null && !schema.isRequired) ? schema.nullReplacement : Number(e.data[schema.propertyName]),
            }
        }));
        records.sort((a, b) => {
            if (typeof a === 'string')
                return -1;
            if (typeof b === 'string')
                return 1;
            return numComparator(a.data[schema.propertyName], b.data[schema.propertyName])
        });

        const nullValues = records.filter(e => typeof e.data[schema.propertyName] !== 'number');
        const sequences = [];
        if (nullValues.length) {
            sequences.push(nullValues);
        }
        const nonNullValues = records.filter(e => typeof e.data[schema.propertyName] === 'number');
        let currentSequence
        if (nonNullValues.length) {
            currentSequence = [nonNullValues[0]];

            for (let i = 1; i < nonNullValues.length; i++) {
                // Check if the current number is consecutive to the last one in the sequence
                if (nonNullValues[i].data[schema.propertyName] === nonNullValues[i - 1].data[schema.propertyName] + 1) {
                    currentSequence.push(nonNullValues[i]);
                } else {
                    // If not consecutive, add the current sequence to the result and start a new one
                    sequences.push(currentSequence);
                    currentSequence = [nonNullValues[i]];
                }
            }
        }
        // Add the last sequence to the result
        if (currentSequence) {
            sequences.push(currentSequence);
        }
        return sequences;
    }, [])

    const dataGridRows = useMemo(() => {
        /**@type {DataGridRow[]}*/
        const flatArray = [];

        // Recursive function to traverse and flatten the grouped records
        function processGroup(group, parents = [], forceLeaf) {
            const isLeaf = !!forceLeaf || !group.commonId; // Check if this is a leaf node (a record)

            const defaultRecord = typeof group?.value?.content === 'string';

            // Prepare the cells object for the current group or record
            const cells = {};

            // Populate cells based on whether it's a group or record
            if (isLeaf) {
                const record = group;
                if (forceLeaf && !!group.schema) {
                    switch (group.schema.type) {
                        case ProductPropertyGroupRecordLeafTypes.range: {
                            const rangeRecords = group.value;
                            for (const parent of parents) {
                                if (parent?.propertyName) {
                                    cells[`__${parent.propertyName}`] = {groupValue: parent.value};
                                    cells[parent.propertyName] = <p {...dataGridCellTextProps}/>;
                                }
                            }
                            if (rangeRecords.length === 0) {
                                cells[group.schema.propertyName] = '';
                            } else if (rangeRecords.length === 1) {
                                cells[group.schema.propertyName] = rangeRecords[0].data[group.schema.propertyName] === null ? (group.schema.nullReplacement ?? "") : rangeRecords[0].data[group.schema.propertyName] ?? ""
                            } else {
                                cells[group.schema.propertyName] =
                                    <div className={'d-flex align-items-center font-weight-normal'} style={{gap: 4}}
                                    >
                                        {rangeRecords[0].data[group.schema.propertyName]}
                                        <Typography
                                            variant={'h5'}
                                            className={'px-2 font-weight-bold'}
                                            sx={{color: 'primary.main', height: 14, lineHeight: 0}}
                                        >
                                            ...
                                        </Typography>
                                        {rangeRecords[rangeRecords.length - 1].data[group.schema.propertyName]}
                                    </div>;
                            }
                            break;
                        }
                        default: {
                            for (const parent of parents) {
                                if (parent?.propertyName) {
                                    cells[`__${parent.propertyName}`] = {groupValue: parent.value};
                                    cells[parent.propertyName] = <p {...dataGridCellTextProps}/>;
                                }
                            }
                            for (const schema of structureSchema) {
                                if (!(`__${schema.propertyName}` in cells)) {
                                    cells[`__${schema.propertyName}`] = {groupValue: group.value.data[schema.propertyName]};
                                }
                                if (schema.propertyName === group.schema.propertyName) {
                                    const shownValue = group.value.data[schema.propertyName] ?? (schema.nullReplacement ?? '');
                                    // Display the group's title in its corresponding column
                                    cells[schema.propertyName] =
                                        <div className={'d-flex align-items-center justify-content-end'}>
                                            <p {...dataGridCellTextProps} className={'mr-2'}>{shownValue}</p>
                                        </div>;
                                } else {
                                    cells[schema.propertyName] = <p {...dataGridCellTextProps}/>;
                                }
                            }
                            break;
                        }
                    }
                } else {
                    // this is for new cells.
                    for (const schema of structureSchema) {
                        cells[`__${schema.propertyName}`] = {groupValue: record.data[schema.propertyName]};
                        cells[schema.propertyName] = <p {...dataGridCellTextProps}/>;
                    }
                    const lastSchema = structureSchema[structureSchema.length - 1];
                    if (lastSchema) {
                        cells[lastSchema.propertyName] =
                            <div className={'d-flex align-items-center'}>
                                <p {...dataGridCellTextProps}>{record.data[lastSchema.propertyName]}</p>
                            </div>;
                    }
                }
            } else {
                for (const parent of parents) {
                    if (parent?.propertyName) {
                        cells[`__${parent.propertyName}`] = {groupValue: parent.value};
                    }
                }
                // If it's a group, display the group's title in its column, and show counts for inner groups
                for (const schema of structureSchema) {
                    if (!(`__${schema.propertyName}` in cells)) {
                        cells[`__${schema.propertyName}`] = {groupValue: group.value};
                    }
                    if (schema.propertyName === group.schema.propertyName) {
                        const shownValue = group.value ?? (schema.nullReplacement ?? '');
                        // Display the group's title in its corresponding column
                        cells[schema.propertyName] =
                            <div className={'d-flex align-items-center justify-content-end'}>
                                <p {...dataGridCellTextProps} className={'mr-2'}>{shownValue}</p>
                            </div>;
                    } else if (group.counts[`${schema.propertyName}Count`] !== undefined) {
                        const count = group.counts[`${schema.propertyName}Count`];
                        const titles = group.titles[`${schema.propertyName}Title`];
                        const titleGroups = schema.type === ProductPropertyGroupRecordLeafTypes.range
                            ? groupRangeRecords(titles?.map(e => ({data: {[schema.propertyName]: e}})) ?? [], schema)
                            : (titles ?? []);

                        // Display the counts for the inner groups in their respective columns
                        // end with y -> ies, end with s -> es, else add s
                        const pluralName = schema.title.endsWith('y') ? `${schema.title.slice(0, -1)}ies` : schema.title.endsWith('s') ? `${schema.title}es` : `${schema.title}s`;
                        if (titleGroups.length <= 5 && !!titles) {
                            if (schema.type === ProductPropertyGroupRecordLeafTypes.range) {
                                cells[schema.propertyName] =
                                    <p {...dataGridCellTextProps} className={'text-xs font-weight-semi-bold grey-3'}>
                                        {
                                            titleGroups.map(e => {
                                                const vals = e.map(f => f.data[schema.propertyName])
                                                if (vals.length === 1) {
                                                    return vals[0];
                                                }
                                                return `${vals[0]}...${vals[vals.length - 1]}`;
                                            }).join(', ')
                                        }
                                    </p>;
                            } else {
                                cells[schema.propertyName] =
                                    <p {...dataGridCellTextProps} className={'text-xs font-weight-semi-bold grey-3'}>
                                        {titles?.map(e => (e === null && !schema.isRequired) ? schema.nullReplacement : e ?? "").join(', ') ?? ""}
                                    </p>;
                            }
                        } else {
                            cells[schema.propertyName] =
                                <p {...dataGridCellTextProps} className={'text-xs font-weight-semi-bold grey-3'}>
                                    {`${count} ${count === 1 ? schema.title : pluralName}`}
                                </p>;
                        }
                    } else {
                        // For parent groups, leave the column empty
                        cells[schema.propertyName] = <p {...dataGridCellTextProps}/>;
                    }
                }
            }

            // Create a flat object for the current group or record
            flatArray.push({
                key: isLeaf
                    ? Array.isArray(group.value)
                        ? `leaves-${group.value.map(e => e.id).join('-')}`
                        : `leaf-${defaultRecord ? group.value.content : group.value}`
                    : group.commonId?.concat(forceLeaf ? '-leaves' : '') ?? createUUId(),
                data: group,
                cells: cells,
            });

            // If the group has children, recursively process them
            if (!isLeaf) {
                const newParents = [...parents, {value: group.value, propertyName: group.schema.propertyName}];
                if (group.records && !!group.records.length) {
                    const recordsSchema = group.schema.nextPropertyName ? structureSchema.find(e => e.propertyName === group.schema.nextPropertyName) : null;
                    switch (recordsSchema?.type) {
                        case ProductPropertyGroupRecordLeafTypes.range: {
                            const rangeRecords = groupRangeRecords(group.records, recordsSchema);
                            rangeRecords.forEach(rangeRecords => processGroup(
                                {
                                    parentCommonId: group.commonId,
                                    schema: recordsSchema,
                                    value: rangeRecords,
                                    approved: group.approved,
                                    counts: {},
                                },
                                newParents,
                                true,
                            ));
                            break;
                        }
                        default: {
                            group.records.forEach(record => processGroup({
                                commonId: group.commonId,
                                schema: recordsSchema,
                                value: record,
                                approved: group.approved,
                                counts: {},
                            }, newParents, true));
                            break;
                        }
                    }
                } else if (group.children && !!group.children.length) {
                    group.children.forEach(child => processGroup(child, newParents));
                }
            }
        }

        // Start processing the root groups
        groupedRecords.forEach(group => processGroup(group));

        return flatArray;
    }, [groupRangeRecords, groupedRecords, structureSchema])

    /**@type {DataGridRowGroup[]} */
    const dataGridGroups = useMemo(() => {
        return structureSchema.map(e => ({
            columnName: `__${e.propertyName}`,
            showOnColumn: e.propertyName,
        }))
    }, [structureSchema])

    /**
     * Expands All the rows of the data-grid.
     * @type {(function())}
     */
    const expandAll = useCallback(async () => {
        setExpandingAll(true);
        const dataGrid = document.querySelector('.property-group-data-grid');
        for (let i = 0; i < structureSchema.length; i++) {
            const toggleButtons = dataGrid?.querySelectorAll('.data-grid-group-toggler-button:not(.open):not(.invisible)');
            for (const toggleButton of toggleButtons) {
                toggleButton.click();
            }
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        setExpandingAll(false);
    }, [structureSchema])

    /**
     * Collapses All the rows of the data-grid.
     * @type {(function())}
     */
    const collapseAll = useCallback(async () => {
        setCollapsingAll(true);
        const dataGrid = document.querySelector('.property-group-data-grid');
        for (let i = 0; i < structureSchema.length; i++) {
            const toggleButtons = dataGrid?.querySelectorAll('.data-grid-group-toggler-button.open:not(.invisible)');
            for (const toggleButton of toggleButtons) {
                toggleButton.click();
            }
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        setCollapsingAll(false);
    }, [structureSchema])

    return (
        <>
            <div className={'d-flex justify-content-between my-3 mx-3 align-items-center'}>
                <div className={'d-flex align-items-center'} style={{gap: 4}}>
                    {
                        !(loadingSchema || loadingRecords) &&
                        <>
                            <Button
                                variant={'outlined'}
                                color={'secondary'}
                                className={'d-flex align-items-center'}
                                disabled={expandingAll || collapsingAll}
                                onClick={() => expandAll()}
                            >
                                <UnfoldMoreRounded className={'icon small'}/>
                                Expand all rows
                            </Button>
                            <Button
                                variant={'outlined'}
                                color={'secondary'}
                                className={'d-flex align-items-center'}
                                disabled={expandingAll || collapsingAll}
                                onClick={() => collapseAll()}
                            >
                                <UnfoldLessRounded className={'icon small'}/>
                                Collapse all rows
                            </Button>
                        </>
                    }
                </div>
            </div>
            <Box sx={{
                '.data-grid-loading-container': {
                    marginTop: `0 !important`,
                },
            }}>
                {
                    structureSchema.length > 1 &&
                    <PartnerDataGrid
                        initialLayoutState={{columns: columns,}}
                        classNames={{toolbar: 'pt-0', container: 'property-group-data-grid'}}
                        rows={dataGridRows}
                        columns={columns}
                        hideToolbar
                        hideFooter
                        state={{
                            loading: {state: loadingRecords || loadingSchema},
                            group: dataGridGroups,
                        }}
                    />
                }
            </Box>
        </>
    );

}

const Description = ({description, loading}) => {

    if (loading) {
        return (
            <div className='loading part-search-log px-2'>
                <div/>
            </div>
        );
    }

    return (
        <Row className='no-gutters justify-content-between align-items-center pt-2'>
            <Col xs={12} className='my-3 mx-2'>
                <div className='text-color-1 text-sm' style={{maxWidth: '100%'}}
                     dangerouslySetInnerHTML={{__html: description}}/>
            </Col>
        </Row>
    );
}

const Note = ({notes, loading}) => {


    if (loading) {
        return (
            <div className='loading part-prices'>
                <div/>
                <div/>
            </div>
        );
    }
    return (
        <Row className='no-gutters justify-content-start align-items-center py-3'>
            {notes?.map((e) => (
                <Col xl={4} lg={6} xs={12} key={e.id} className="mb-3 mr-3">
                    <div className='background-secondary-color-3 rounded-lg p-3'>
                        <p className='text-md font-weight-bold text-color-1'>{e.title}</p>
                        <div className='text-sm text-lowercase text-color-1' dangerouslySetInnerHTML={{__html: e.msg}}/>
                    </div>
                </Col>
            ))}
        </Row>
    );
}

const TechnicalFiles = ({files, loading}) => {
    const [previewDialog, setPreviewDialog] = useState({open: false, data: null});

    /**
     * Fetch the preview data of the file
     * @return {{fileName, type: string, url}}
     */
    const getPreviewData = (technicalFile) => {
        let type;
        switch (technicalFile?.type?.id) {
            case ProductTechnicalFileTypes.HTML:
                type = FilePreviewTypes.html;
                break;
            case ProductTechnicalFileTypes.PDF:
                type = FilePreviewTypes.pdf;
                break;
            case ProductTechnicalFileTypes.IMAGE:
                type = FilePreviewTypes.image;
                break;
            case ProductTechnicalFileTypes.unknown:
            default:
                type = FilePreviewTypes.unknown;
                break;
        }
        return {
            url: technicalFile.fileAddress,
            type: type,
            fileName: technicalFile.name,
        }
    }

    /**
     * Opens the technical file preview dialog.
     * @param {any} technicalFile the technical file
     */
    const openFilePreviewDialog = (technicalFile) => {
        setPreviewDialog({
            open: true,
            data: getPreviewData(technicalFile)
        })
    }


    if (loading) {
        return (
            <div className='loading part-prices'>
                <div/>
                <div/>
                <div/>
                <div/>
            </div>
        );
    }

    return (
        <>
            <Row className={'no-gutters'}>
                {
                    files?.map((technicalFile) => (
                        <TechnicalFile
                            key={technicalFile.id}
                            technicalFile={technicalFile}
                            openFilePreviewDialog={openFilePreviewDialog}
                        />
                    ))
                }
            </Row>
            <FilePreviewModal
                data={previewDialog.data}
                open={previewDialog.open}
                setOpen={() => setPreviewDialog({open: false, data: null})}
            />
        </>
    );
}

const TechnicalFile = ({technicalFile, openFilePreviewDialog}) => {

    /**
     * Downloads the file of the technical file for the user by fetching the file then downloading it using an <a> tag.
     */
    const _downloadFile = async (e, file) => {
        e.preventDefault();
        e.stopPropagation();
        await fetch(file?.fileAddress)
            .then(resp => {
                if (resp.status !== 200) return undefined;
                return resp.blob();
            })
            .then(blob => {
                if (!blob) return;
                downloadFile(blob, file?.name);
            })
            .catch((e) => {
                console.log(e?.response?.data);
            });
    }

    /**
     * Renders the file icon based on the provided type.
     * @return {JSX.Element}
     */
    const fileIcon = useMemo(() => {
        switch (technicalFile?.type?.id) {
            case ProductTechnicalFileTypes.PDF:
                return <Pdf className={'file-icon'}/>;
            case ProductTechnicalFileTypes.HTML:
                return <Html className={'file-icon'}/>;
            case ProductTechnicalFileTypes.IMAGE:
                return <Image className={'file-icon'}/>;
            default:
                return <Unknown className={'file-icon'}/>;
        }
    }, [technicalFile])

    return (
        <>
            <Col md={6} xs={12} className="my-2 px-2">
                <div className="lookup-product-info-technical-file"
                     onClick={() => openFilePreviewDialog(technicalFile)}>
                    <div className={'d-flex w-100 align-items-start'}>
                        <div className={'image'}>
                            {fileIcon}
                        </div>
                        <div className={'w-100 d-flex flex-column'}>
                            <div className={'d-flex justify-content-between align-items-center'}>
                                <h6>
                                    {technicalFile?.name ?? ''}
                                </h6>
                                <div className={'d-flex justify-content-end align-items-center'}>
                                    <IconButton
                                        onPressed={(e) => _downloadFile(e, technicalFile)}
                                        color={'secondary'}
                                    >
                                        <CloudDownload className={'icon normal'}/>
                                    </IconButton>
                                </div>
                            </div>
                            <p className={'text-wrap'}>
                                {
                                    technicalFile?.description
                                        ?.substring(0, 99)
                                        ?.concat(technicalFile?.description?.length > 99 ? '...' : '')
                                }
                            </p>
                        </div>
                    </div>
                </div>
            </Col>
        </>
    );
}

export default LookupProductInformationViewInfoSection
